import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { Radar, LayoutTemplate, Users, Zap } from "lucide-react";
import Button from "../../../components/Buttons/Button";
import Loader from "../../../components/Loaders/Loader";
import FeatureAddonPaywallDialog from "../../../components/FeatureAddons/FeatureAddonPaywallDialog";
import WhiteLabelBrandingModal from "../../../components/FeatureAddons/WhiteLabelBrandingModal";
import WhiteLabelBillingRow from "./WhiteLabelBillingRow";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import ModalCancelFeatureAddon from "../../../components/Modal/ModalCancelFeatureAddon";
import ModalManageExtraSeats from "../../../components/Modal/ModalManageExtraSeats";
import FeatureAddonBillingRow, {
  ContainerFeatureAddonBillingRows,
} from "./FeatureAddonBillingRow";
import { presentSeatsApiError } from "../../../hooks/useChangeSeats";
import useSeatsCheckoutReturn from "../../../hooks/useSeatsCheckoutReturn";
import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import useAgencyIsAppSumo from "../../../hooks/useAgencyIsAppSumo";
import { useAgencyFeatureAddonList } from "../../../hooks/useAgencyFeatureAddons";
import useWhiteLabelPurchase from "../../../hooks/useWhiteLabelPurchase";
import useWhiteLabelCheckoutReturn from "../../../hooks/useWhiteLabelCheckoutReturn";
import {
  FEATURE_ADDON_KEYS,
  startCancelFeatureAddon,
  startCompleteFeatureAddonPaymentMethod,
  startCreateFeatureAddonCheckout,
  startReactivateFeatureAddon,
  startUpdateFeatureAddonPaymentMethod,
} from "../../../actions/featureAddons";
import {
  FEATURE_ADDON_FREE_TIER_ID,
  isFeatureAddonLimitsEligible,
} from "../../../utils/featureAddonCatalog";
import { shouldShowWhiteLabelBillingRow } from "../../../utils/whiteLabelAddon";
import { ALERT_ICON_TYPE_SUCCESS } from "../../../utils/constants";
import {
  SEATS_RETURN_TO,
  planBenefitsIncludeUsers,
  seatsBillingQuantity,
  seatsBillingSchedule,
  shouldShowSeatsBillingRow,
} from "../../../utils/extraSeats";
import {
  ALERT_PAYMENT_METHOD_CHANGED_TITLE,
  BUTTON_CANCEL_SUBSCRIPTION,
  BUTTON_CHANGE_PAYMENT_METHODS,
  BUTTON_REACTIVATE_SUBSCRIPTION,
  FEATURE_ADDON_BILLING_CANCEL_DESCRIPTION,
  FEATURE_ADDON_BILLING_CANCELLED_TITLE,
  FEATURE_ADDON_BILLING_CANCELS,
  FEATURE_ADDON_BILLING_REACTIVATED_TITLE,
  FEATURE_ADDON_BILLING_RENEWS,
  EXTRA_SEATS_BILLING_ROW_TITLE,
  EXTRA_SEATS_CHANGE_SAVED,
  EXTRA_SEATS_MANAGE,
  EXTRA_SEATS_QUANTITY_BADGE,
  FEATURE_ADDON_BILLING_TITLE,
  FEATURE_ADDON_BILLING_UPGRADE,
  FEATURE_ADDON_NAME_AUTOMATIONS,
  FEATURE_ADDON_NAME_COMPETITORS,
  FEATURE_ADDON_NAME_TEMPLATES,
  FEATURE_ADDON_PAYWALL_FREE,
  FEATURE_ADDON_PAYWALL_PER_MONTH,
  FEATURE_ADDON_PAYWALL_TIER_CTA,
  FEATURE_ADDON_PAYWALL_TIER_NAME,
  IN_A_FEW_MINUTES_YOUR_INVOICE_WILL_BE_AVAILABLE,
  UPGRADE_SUBSCRIPTION_SUCCESSFUL,
} from "../../../i18n/keysTranslations";

const ADDON_ROWS = [
  {
    key: FEATURE_ADDON_KEYS.AUTOMATIONS,
    nameKey: FEATURE_ADDON_NAME_AUTOMATIONS,
    Icon: Zap,
  },
  {
    key: FEATURE_ADDON_KEYS.TEMPLATES,
    nameKey: FEATURE_ADDON_NAME_TEMPLATES,
    Icon: LayoutTemplate,
  },
  {
    key: FEATURE_ADDON_KEYS.COMPETITORS,
    nameKey: FEATURE_ADDON_NAME_COMPETITORS,
    Icon: Radar,
  },
];

const addonStatusBadge = ({ label, paid }) => (
  <Typography
    component="span"
    sx={{
      fontSize: 12,
      fontWeight: 600,
      color: paid ? "#1B7F4E" : "text.secondary",
      backgroundColor: paid ? "#E7F7EF" : "rgba(0,0,0,0.05)",
      borderRadius: "999px",
      px: 1,
      py: 0.25,
    }}
  >
    {label}
  </Typography>
);

const tierLabel = ({ tierId, t }) => {
  if (!tierId || tierId === FEATURE_ADDON_FREE_TIER_ID) {
    return t(FEATURE_ADDON_PAYWALL_FREE);
  }
  const match = String(tierId).match(/(\d+)$/);
  return t(FEATURE_ADDON_PAYWALL_TIER_NAME, {
    index: match ? match[1] : tierId,
  });
};

const highestPaidTierId = (tiers = []) => {
  const paid = tiers.filter(
    (tier) => tier?.id && tier.id !== FEATURE_ADDON_FREE_TIER_ID
  );
  return paid[paid.length - 1]?.id || null;
};

const subscriptionIdOf = (agencyAddon) => {
  const id = agencyAddon?.stripeSubscriptionID;
  return typeof id === "string" && id.trim() ? id.trim() : "";
};

const AddonRow = ({
  row,
  status,
  busy,
  reserveMenu,
  onSubscribe,
  onChangePaymentMethod,
  onCancel,
  onReactivate,
}) => {
  const { t } = useTranslationApp();
  const tierId = status?.effective?.tierId || FEATURE_ADDON_FREE_TIER_ID;
  const isFree = tierId === FEATURE_ADDON_FREE_TIER_ID;
  const tier = (status?.tiers || []).find((item) => item.id === tierId);
  const price = Number(tier?.priceMonthly) || 0;
  const cancelDateTime = status?.agencyAddon?.cancelDateTime;
  const renewDate = status?.agencyAddon?.renewDate;
  const hasSubscription = Boolean(subscriptionIdOf(status?.agencyAddon));
  const cancelScheduled =
    cancelDateTime != null && new Date().getTime() < Number(cancelDateTime);
  const isHighest = !isFree && tierId === highestPaidTierId(status?.tiers);
  const scheduleDate = cancelScheduled ? cancelDateTime : renewDate;

  return (
    <FeatureAddonBillingRow
      Icon={row.Icon}
      title={t(row.nameKey)}
      busy={busy}
      reserveMenu={reserveMenu}
      badge={addonStatusBadge({
        label: tierLabel({ tierId, t }),
        paid: !isFree,
      })}
      details={
        !isFree && scheduleDate ? (
          <Typography variant="body2" color="text.secondary">
            {t(
              cancelScheduled
                ? FEATURE_ADDON_BILLING_CANCELS
                : FEATURE_ADDON_BILLING_RENEWS,
              {
                date: new Date(Number(scheduleDate)),
              }
            )}
          </Typography>
        ) : null
      }
      actions={
        <>
          {isFree ? null : (
            <Box
              sx={{
                textAlign: { xs: "left", sm: "right" },
                minWidth: { sm: 72 },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5 }}>
                <Typography sx={{ fontWeight: 700 }}>${price}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {t(FEATURE_ADDON_PAYWALL_PER_MONTH)}
                </Typography>
              </Box>
            </Box>
          )}
          {isHighest ? null : (
            <Button
              color="secondary"
              onClick={() => onSubscribe(row.key)}
              sx={{ width: { xs: "100%", sm: "auto" } }}
            >
              {t(
                isFree
                  ? FEATURE_ADDON_PAYWALL_TIER_CTA
                  : FEATURE_ADDON_BILLING_UPGRADE
              )}
            </Button>
          )}
        </>
      }
      menuItems={
        hasSubscription
          ? [
              {
                key: "paymentMethod",
                label: t(BUTTON_CHANGE_PAYMENT_METHODS),
                onClick: () => onChangePaymentMethod(row.key),
              },
              cancelScheduled
                ? {
                    key: "reactivate",
                    label: t(BUTTON_REACTIVATE_SUBSCRIPTION),
                    onClick: () => onReactivate(row.key),
                  }
                : {
                    key: "cancel",
                    label: t(BUTTON_CANCEL_SUBSCRIPTION),
                    onClick: () =>
                      onCancel(row.key, renewDate || cancelDateTime),
                  },
            ]
          : []
      }
    />
  );
};

const SeatsAddonRow = ({
  addonNode,
  planUsersFallback = null,
  menuBusy,
  reserveMenu = false,
  onChangePaymentMethod,
  onCancel,
  onReactivate,
}) => {
  const { t } = useTranslationApp();
  const [modalOpen, setModalOpen] = useState(false);
  const quantity = seatsBillingQuantity(addonNode);
  const minQuantity = Number(addonNode?.minQuantity);
  const min = Number.isFinite(minQuantity) && minQuantity > 0 ? minQuantity : 0;
  const schedule = seatsBillingSchedule({ addonNode });
  const hasSubscription = Boolean(subscriptionIdOf(addonNode));

  return (
    <>
      <FeatureAddonBillingRow
        Icon={Users}
        title={t(EXTRA_SEATS_BILLING_ROW_TITLE)}
        busy={menuBusy}
        reserveMenu={reserveMenu}
        badge={addonStatusBadge({
          label: t(EXTRA_SEATS_QUANTITY_BADGE, { n: quantity }),
          paid: quantity > 0,
        })}
        details={
          schedule ? (
            <Typography variant="body2" color="text.secondary">
              {t(
                schedule.cancelScheduled
                  ? FEATURE_ADDON_BILLING_CANCELS
                  : FEATURE_ADDON_BILLING_RENEWS,
                { date: schedule.date }
              )}
            </Typography>
          ) : null
        }
        actions={
          <Button
            color="secondary"
            disabled={menuBusy}
            onClick={() => setModalOpen(true)}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            {t(EXTRA_SEATS_MANAGE)}
          </Button>
        }
        menuItems={
          hasSubscription
            ? [
                {
                  key: "paymentMethod",
                  label: t(BUTTON_CHANGE_PAYMENT_METHODS),
                  onClick: () =>
                    onChangePaymentMethod(FEATURE_ADDON_KEYS.SEATS),
                },
                schedule?.cancelScheduled
                  ? {
                      key: "reactivate",
                      label: t(BUTTON_REACTIVATE_SUBSCRIPTION),
                      onClick: () => onReactivate(FEATURE_ADDON_KEYS.SEATS),
                    }
                  : {
                      key: "cancel",
                      label: t(BUTTON_CANCEL_SUBSCRIPTION),
                      disabled: min > 0,
                      onClick: () =>
                        onCancel(
                          FEATURE_ADDON_KEYS.SEATS,
                          addonNode?.renewDate || addonNode?.cancelDateTime
                        ),
                    },
              ]
            : []
        }
      />
      <ModalManageExtraSeats
        modalOpen={modalOpen}
        onCloseModal={() => setModalOpen(false)}
        onCompleted={() => setModalOpen(false)}
        seatsStatus={addonNode}
        planUsersFallback={planUsersFallback}
        purpose="manage"
        returnTo={SEATS_RETURN_TO.BILLING}
      />
    </>
  );
};

const FeatureAddonsBillingSection = () => {
  const { t } = useTranslationApp();
  const dispatch = useDispatchApp();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { isAppSumo, isAppSumoResolved } = useAgencyIsAppSumo();
  const planPrice = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo?.price
  );
  const planUsers = useSelectorApp(
    (state) => state.payments.subscriptionPlanInfo?.benefits?.users
  );
  const eligible = isFeatureAddonLimitsEligible({
    isAppSumo,
    isAppSumoResolved,
    planPrice,
  });
  const includeUsers = planBenefitsIncludeUsers(planUsers);
  const { statuses, seatsAddon, isLoading } = useAgencyFeatureAddonList({
    enabled: eligible || includeUsers,
  });
  const showSeats = shouldShowSeatsBillingRow({
    addonNode: seatsAddon,
    planUsers,
  });
  const [paywallKey, setPaywallKey] = useState(null);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [cancelRequest, setCancelRequest] = useState(null);
  const [brandingOpen, setBrandingOpen] = useState(false);
  const whiteLabelPurchase = useWhiteLabelPurchase();
  const showWhiteLabel = shouldShowWhiteLabelBillingRow(
    whiteLabelPurchase.status
  );

  useWhiteLabelCheckoutReturn({
    status: whiteLabelPurchase.status,
    refreshStatus: whiteLabelPurchase.refreshStatus,
    onOpenBranding: () => setBrandingOpen(true),
  });

  const paywallStatus =
    paywallKey === FEATURE_ADDON_KEYS.WHITE_LABEL
      ? whiteLabelPurchase.status
      : statuses.find((item) => item.addonKey === paywallKey) || null;
  const reserveMenu =
    statuses.some((item) => Boolean(subscriptionIdOf(item?.agencyAddon))) ||
    Boolean(subscriptionIdOf(seatsAddon)) ||
    Boolean(subscriptionIdOf(whiteLabelPurchase.status?.agencyAddon));

  useSeatsCheckoutReturn({
    onPaid: async () => {
      SimpleAlert({
        title: t(EXTRA_SEATS_CHANGE_SAVED),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
    },
  });

  const reportSeatsError = useCallback(
    async ({ addonKey, result, intent = "" }) => {
      if (addonKey !== FEATURE_ADDON_KEYS.SEATS || result?.ok !== false) {
        return;
      }
      await presentSeatsApiError({ result, t, navigate, intent });
    },
    [navigate, t]
  );

  useEffect(() => {
    const flag = params.get("featureAddonPaymentMethod");
    const addonKey = params.get("addonKey");
    const sessionId = params.get("session_id");
    if (flag === "cancel") {
      const next = new URLSearchParams(params);
      next.delete("featureAddonPaymentMethod");
      setParams(next, { replace: true });
      return undefined;
    }
    if (flag !== "success" || !addonKey || !sessionId) return undefined;

    let cancelled = false;
    dispatch(
      startCompleteFeatureAddonPaymentMethod({ addonKey, sessionId })
    ).then((result) => {
      if (cancelled) return;
      if (result?.ok) {
        SimpleAlert({
          title: t(ALERT_PAYMENT_METHOD_CHANGED_TITLE),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
      }
    });

    const next = new URLSearchParams(params);
    next.delete("featureAddonPaymentMethod");
    next.delete("addonKey");
    next.delete("session_id");
    setParams(next, { replace: true });
    return () => {
      cancelled = true;
    };
  }, [dispatch, params, setParams, t]);

  const checkoutTier = useCallback(
    async (tierId) => {
      if (!paywallKey || !tierId) return { ok: false };
      setIsCheckingOut(true);
      const result = await dispatch(
        startCreateFeatureAddonCheckout({
          addonKey: paywallKey,
          tierId,
          origin: window.location.origin,
        })
      );
      setIsCheckingOut(false);
      if (result?.ok && result?.data?.upgraded) {
        SimpleAlert({
          title: t(UPGRADE_SUBSCRIPTION_SUCCESSFUL),
          text: t(IN_A_FEW_MINUTES_YOUR_INVOICE_WILL_BE_AVAILABLE),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
        setPaywallKey(null);
        return result;
      }
      if (result?.ok && result?.data?.paymentUrl) {
        window.location.assign(result.data.paymentUrl);
      }
      return result;
    },
    [dispatch, paywallKey, t]
  );

  const onChangePaymentMethod = useCallback(
    async (addonKey) => {
      setBusyKey(addonKey);
      const result = await dispatch(
        startUpdateFeatureAddonPaymentMethod({
          addonKey,
          origin: window.location.origin,
        })
      );
      setBusyKey(null);
      if (result?.ok && result?.data?.url) {
        window.location.assign(result.data.url);
        return;
      }
      await reportSeatsError({ addonKey, result });
    },
    [dispatch, reportSeatsError]
  );

  const onCancel = useCallback((addonKey, renewDate) => {
    setCancelRequest({ addonKey, renewDate });
  }, []);

  const onConfirmCancel = useCallback(async () => {
    if (!cancelRequest?.addonKey) return;
    const { addonKey } = cancelRequest;
    setBusyKey(addonKey);
    const result = await dispatch(startCancelFeatureAddon({ addonKey }));
    setBusyKey(null);
    if (result?.ok) {
      setCancelRequest(null);
      SimpleAlert({
        title: t(FEATURE_ADDON_BILLING_CANCELLED_TITLE),
        text: t(FEATURE_ADDON_BILLING_CANCEL_DESCRIPTION, {
          date: result?.data?.cancelDateTime
            ? new Date(Number(result.data.cancelDateTime))
            : new Date(),
        }),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
      return;
    }
    await reportSeatsError({ addonKey, result });
  }, [cancelRequest, dispatch, reportSeatsError, t]);

  const onReactivate = useCallback(
    async (addonKey) => {
      setBusyKey(addonKey);
      const result = await dispatch(startReactivateFeatureAddon({ addonKey }));
      setBusyKey(null);
      if (result?.ok) {
        SimpleAlert({
          title: t(FEATURE_ADDON_BILLING_REACTIVATED_TITLE),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
        return;
      }
      await reportSeatsError({
        addonKey,
        result,
        intent: "reactivate",
      });
    },
    [dispatch, reportSeatsError, t]
  );

  if (!eligible && !showWhiteLabel && !showSeats) return null;

  return (
    <Box sx={{ width: "100%", mb: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
        {t(FEATURE_ADDON_BILLING_TITLE)}
      </Typography>
      <ContainerFeatureAddonBillingRows>
        {eligible && isLoading ? <Loader /> : null}
        {eligible
          ? ADDON_ROWS.map((row) => (
              <AddonRow
                key={row.key}
                row={row}
                status={statuses.find((item) => item.addonKey === row.key)}
                busy={busyKey === row.key}
                reserveMenu={reserveMenu}
                onSubscribe={setPaywallKey}
                onChangePaymentMethod={onChangePaymentMethod}
                onCancel={onCancel}
                onReactivate={onReactivate}
              />
            ))
          : null}
        {showWhiteLabel ? (
          <WhiteLabelBillingRow
            status={whiteLabelPurchase.status}
            busy={busyKey === FEATURE_ADDON_KEYS.WHITE_LABEL}
            canManage={whiteLabelPurchase.canManage}
            reserveMenu={reserveMenu}
            onBuy={() => setPaywallKey(FEATURE_ADDON_KEYS.WHITE_LABEL)}
            onEditBrand={() => setBrandingOpen(true)}
            onChangePaymentMethod={() =>
              onChangePaymentMethod(FEATURE_ADDON_KEYS.WHITE_LABEL)
            }
            onCancel={(renewDate) =>
              onCancel(FEATURE_ADDON_KEYS.WHITE_LABEL, renewDate)
            }
            onReactivate={() => onReactivate(FEATURE_ADDON_KEYS.WHITE_LABEL)}
          />
        ) : null}
        {showSeats ? (
          <SeatsAddonRow
            addonNode={seatsAddon}
            planUsersFallback={planUsers}
            menuBusy={busyKey === FEATURE_ADDON_KEYS.SEATS}
            reserveMenu={reserveMenu}
            onChangePaymentMethod={onChangePaymentMethod}
            onCancel={onCancel}
            onReactivate={onReactivate}
          />
        ) : null}
      </ContainerFeatureAddonBillingRows>
      <ModalCancelFeatureAddon
        modalOpen={Boolean(cancelRequest)}
        onCloseModal={() => setCancelRequest(null)}
        onConfirm={onConfirmCancel}
        loading={Boolean(cancelRequest) && busyKey === cancelRequest?.addonKey}
        renewDate={cancelRequest?.renewDate}
      />
      <FeatureAddonPaywallDialog
        open={Boolean(paywallKey)}
        onClose={() => setPaywallKey(null)}
        status={paywallStatus}
        addonKey={paywallKey}
        isLoading={
          paywallKey === FEATURE_ADDON_KEYS.WHITE_LABEL
            ? whiteLabelPurchase.isLoading
            : isLoading
        }
        isCheckingOut={isCheckingOut}
        onCheckoutTier={checkoutTier}
      />
      <WhiteLabelBrandingModal
        modalOpen={brandingOpen}
        onCloseModal={() => setBrandingOpen(false)}
        branding={whiteLabelPurchase.whiteLabel?.branding}
        onSubmitted={whiteLabelPurchase.refreshStatus}
      />
    </Box>
  );
};

export default FeatureAddonsBillingSection;
